import React, { useCallback } from 'react'
import classNames from 'classnames'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { CaptionSM } from '@/atoms/Text'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { ProgressOverlay } from '@/molecules/MediaCard/ProgressOverlay'
import {
  BrandLandingTabItemInteraction,
  useIsolatedCardInteractions,
  useImpressionTracking,
} from '@/services/Analytics/ContentInteractions'
import { VideoMetaData } from '@/services/ProjectsService'
import { useTranslate } from '@/utils/translate/translate-client'

export interface BonusVideoCardProps {
  video: VideoMetaData
  projectSlug: string
  itemIndex: number
  pagePlacement: number
  durationLabel?: string
  onVideoClick?: (video: VideoMetaData) => void
  includeHoverScale?: boolean
}

export const BonusVideoCard: React.FC<BonusVideoCardProps> = ({
  video,
  projectSlug,
  itemIndex,
  pagePlacement,
  durationLabel,
  onVideoClick,
  includeHoverScale,
}) => {
  const { t } = useTranslate('watch')
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9,
  })

  const videoId = video.guid
  const videoThumbnailPath = video.posterLandscapeCloudinaryPath
  const href = `/watch/${projectSlug}/videos/${video.page}/${video.guid}`

  let progress = 0
  if (video?.watchPosition?.position && video.source?.duration) {
    progress = (video?.watchPosition?.position / video?.source.duration) * 100
  }

  const createInteraction = useCallback(
    (item: VideoMetaData) => {
      const id = item.guid
      const thumbnailPath = item.posterLandscapeCloudinaryPath
      if (!id || !thumbnailPath) return null

      return new BrandLandingTabItemInteraction({
        id,
        __typename: 'ContentLivestream',
        attribution: {
          itemIndex,
          pagePlacement,
          image: {
            cloudinaryPath: thumbnailPath,
          },
        },
      })
        .withSurface('brand_landing')
        .withPlacement('extras_tab', pagePlacement, itemIndex, 'vertical_list')
        .withImageArtwork(thumbnailPath, 'landscape_display')
    },
    [itemIndex, pagePlacement],
  )

  const { handleMouseEnter, handleMouseLeave, handleClick } = useIsolatedCardInteractions({
    contentId: videoId,
    item: video,
    itemIndex,
    createInteraction,
    onClickTrack: () => onVideoClick?.(video),
  })

  useImpressionTracking({
    isVisible,
    contentId: videoId,
    item: video,
    itemIndex,
    createInteraction,
  })

  return (
    <div ref={cardRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <InternalLink href={href} className="group cursor-pointer" onClick={handleClick}>
        <div className="flex w-full flex-row items-start gap-4">
          <div className="shrink-0 snap-center snap-always">
            <div
              className={classNames(
                'relative overflow-hidden rounded-lg transition-transform duration-[400ms] ease-in-out',
                {
                  'md:group-hover:scale-[1.05]': includeHoverScale,
                },
              )}
            >
              <Image
                alt={`${video.title} Bonus Content Thumbnail`}
                src={videoThumbnailPath || ''}
                className="h-full max-h-[101px] w-full max-w-[180px] md:max-h-[144px] md:max-w-[256px] lg:max-h-[169px] lg:max-w-[300px]"
                width={300}
                height={169}
              />
              <PlayButtonThumbnailOverlay />
              <ProgressOverlay progress={progress} />
            </div>
          </div>
          <div className="space-between flex h-full w-full flex-row items-center gap-x-4 lg:items-start lg:gap-x-0">
            <div className="flex grow-1 flex-col gap-[6px]">
              <CaptionSM color="core-gray-500" className="md:photon-caption-md lg:photon-caption-lg">
                {t('bonus', 'Bonus')}
              </CaptionSM>
              <CaptionSM weight="bold" className="lg:photon-title-md line-clamp-2 lg:line-clamp-4">
                {video.subtitle}
              </CaptionSM>
              {durationLabel && (
                <CaptionSM color="core-gray-500" className="md:photon-paragraph-md">
                  {durationLabel}
                </CaptionSM>
              )}
            </div>
            <div className="lg:hidden">
              <CaretRightIcon size={24} color="white" />
            </div>
          </div>
        </div>
      </InternalLink>
    </div>
  )
}
